/**
 * Pure helpers for the Payments page's application (tenant) context.
 *
 * `GET /api/v1/payments` scopes rows to one application. With a user token the
 * backend reads that application from the `X-Application-ID` header, so the page
 * must pick one explicitly — there is no server-side default. Kept free of React
 * and DOM so the decisions can be unit-tested without a browser.
 */

export const APPLICATION_ID_HEADER = 'X-Application-ID'
export const STORAGE_KEY = 'payments.application_id'

export interface ApplicationOption {
  id: number
  name: string
}

/** Parse a stored value; anything that is not a positive integer is "nothing stored". */
export function parseStoredApplicationId(raw: string | null | undefined): number | null {
  if (raw == null) return null
  const trimmed = raw.trim()
  if (!/^\d+$/.test(trimmed)) return null
  const id = Number(trimmed)
  return id > 0 ? id : null
}

/**
 * Choose the initially selected application.
 * - A stored id that still exists wins.
 * - Exactly one application → preselect it (a single-app tenant should not be prompted).
 * - Otherwise nothing is selected and the page must prompt.
 */
export function resolveInitialSelection(
  storedId: number | null,
  applications: readonly ApplicationOption[]
): number | null {
  if (storedId != null && applications.some((a) => a.id === storedId)) return storedId
  if (applications.length === 1) return applications[0].id
  return null
}

export function tenantHeaders(applicationId: number): Record<string, string> {
  return { [APPLICATION_ID_HEADER]: String(applicationId) }
}

export type ListView = 'loading' | 'error' | 'select-application' | 'empty' | 'table'

export interface ListViewInput {
  loading: boolean
  error: string | null
  selectedApplicationId: number | null
  rowCount: number
  /** True only after a successful list response for the current selection. */
  loaded: boolean
}

/**
 * Decide what the table area shows. Order matters: an error must never be
 * masked as "no payments", and "no payments" is only claimed after a successful load.
 */
export function resolveListView(input: ListViewInput): ListView {
  if (input.error) return 'error'
  if (input.selectedApplicationId == null) return 'select-application'
  if (input.loading || !input.loaded) return 'loading'
  return input.rowCount === 0 ? 'empty' : 'table'
}

export function readStoredApplicationId(storage: Pick<Storage, 'getItem'> | null): number | null {
  try {
    return parseStoredApplicationId(storage?.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

export function writeStoredApplicationId(
  storage: Pick<Storage, 'setItem' | 'removeItem'> | null,
  id: number | null
): void {
  try {
    if (id == null) storage?.removeItem(STORAGE_KEY)
    else storage?.setItem(STORAGE_KEY, String(id))
  } catch {
    // Storage unavailable (private mode, quota) — selection just won't persist.
  }
}
